import { Component, forwardRef, inject, signal } from '@angular/core';
import { Vehicle } from '../../models/vehicle.model';
import { VehiclesService } from '../vehicles.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AdminService } from '../../admin/services/admin.service';
import { RoleService } from '../../role/role.service';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder } from '@angular/forms';
import { catchError, EMPTY } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { AsyncPipe, CurrencyPipe, DatePipe, Location, TitleCasePipe } from '@angular/common';
import { BookingComponent } from '../../main/booking/booking.component';
import { MatToolbar } from '@angular/material/toolbar';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { GoToDetailsDirective } from '../../shared/directives/go-to-details.directive';
import { OrderComponent } from '../../main/order/order.component';
import { IdentifierPipe } from '../../shared/pipes/identifier.pipe';

@Component({
  selector: 'app-vehicle-details',
  imports: [
    AsyncPipe,
    CurrencyPipe,
    DatePipe,
    forwardRef(() => GoToDetailsDirective),
    IdentifierPipe,
    MatIcon,
    MatToolbar,
    MatTooltip,
    TitleCasePipe,
  ],
  templateUrl: './vehicle-details.component.html',
  styleUrl: './vehicle-details.component.scss'
})
export class VehicleDetailsComponent {
  vehicle = signal<Vehicle>(null);

  company: any;

  company_logo: string;

  forbidden = signal(false);

  loading = signal(false);

  saving = signal(false);

  private readonly fb = inject(FormBuilder);

  private readonly vehicleService = inject(VehiclesService);

  vehicleForm = this.fb.group({
    brand_id: this.fb.control(''),
    brand_model_id: this.fb.control(''),
    chasis: this.fb.control(''),
    identifier: this.fb.control(''),
    client_id: this.fb.control(''),
  });


  private readonly data = inject(MAT_DIALOG_DATA, { optional: true });
  private readonly matDialogRef = inject(MatDialogRef<VehicleDetailsComponent>, { optional: true });
  private readonly location = inject(Location);
  public readonly adminService = inject(AdminService);
  public readonly roleService = inject(RoleService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly dialog = inject(MatDialog);


  ngOnInit() {
    this.activatedRoute.params.forEach((params) => {
      if (params.id) {
        this.load(params.id);
      } else {
        this.load(this.data.id);
      }
    });
    this.company_logo = this.adminService.local$.getValue().preferences.logo || 'assets/images/logo-anfruns.png';
    this.company = this.adminService.local$.getValue();
  }

  load(id) {
    this.loading.set(true);
    this.vehicleService.get(id).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 403) {
          this.forbidden.set(true);
        } else {
          console.error(error);
        }
        this.loading.set(true);

        return EMPTY;
      }),
    ).subscribe((response) => { 
      this.loading.set(false);

      this.vehicleForm.patchValue(response.body as any);
      this.vehicle.set(response.body);
    })
  }

  goBack() {
    if (this.matDialogRef instanceof MatDialogRef) {
      this.matDialogRef.close();
    } else {
      this.location.back();
    }
  }

  openOrder(order) {
    if (order.correlative_id) {
      this.dialog.open(OrderComponent, { data: order.id, minWidth: '80%' });
    } else {
      this.dialog.open(BookingComponent, { data: { id: order.id }, minWidth: '80%' });
    }
  }

  createBooking() {
    this.dialog.open(BookingComponent, {
      minWidth: '80%',
      data: {
        object: this.vehicle(),
        client: this.vehicle().client,
      },
    });
  }

  // saveVehicle() {
  //   this.saving.set(true);
  //   this.vehicleService.update(this.client().id, this.vehicleForm.value).pipe(
  //     catchError((error: HttpErrorResponse) => {
  //       console.error(error);
  //       return of(null);
  //     }),
  //   ).subscribe((response) => {
  //     this.saving.set(false);
  //     if (!response) {
  //       return;
  //     }

  //     this.client.set(response.body);
  //   });
  // }

  sortOrders(vehicles) {
    return vehicles.sort((a, b) => {
      const endedAtA = a.orders?.at(0)?.ended_at;
      const endedAtB = b.orders?.at(0)?.ended_at;
      
      // First prioritize vehicles with unfinished orders
      if (!endedAtA && endedAtB) {
        return -1; // a comes first (has unfinished order)
      }

      if (endedAtA && !endedAtB) {
        return 1; // b comes first (has unfinished order)
      }

      // Handle null/undefined years by treating them as the oldest (lowest priority)
      const yearA = a.year ?? -Infinity;
      const yearB = b.year ?? -Infinity;
      
      // Sort by year in descending order (newer first)
      if (yearB !== yearA) {
        return yearB - yearA;
      }
      
      // If years are equal, sort by kms in ascending order (lower first)
      return (a.kms || 0) - (b.kms || 0);
    });
  }
}
